<script setup lang="ts">
import { VWorkspaceContext } from "@app/core";
import type {
  WorkspaceContextPlacement,
  WorkspaceContextSection,
  WorkspaceContextUser,
} from "@app/core";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import { usePartnershipStore } from "../store/partnershipStore";
import { PARTNERSHIP_LABELS, type Partnership } from "../types";

import { RouteNames, type RouteMeta } from "@/app/router/types";
import {
  findFirstAccessibleRoute,
  getEffectivePermissions,
  hasPermissions,
  routeMatchesPartnership,
} from "@/app/router/utils/guards";
import { useAuthStore } from "@/features/auth";
import { Permissions } from "@/shared/constants";
import { useGlobalFiltersStore } from "@/shared/stores/useGlobalFiltersStore";
import { useVendorFiltersStore } from "@/shared/stores/useVendorFiltersStore";

const { collapsed = false, placement = "bottom-left" } = defineProps<{
  collapsed?: boolean
  placement?: WorkspaceContextPlacement
}>();

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const partnershipStore = usePartnershipStore();
const filtersStore = useGlobalFiltersStore();
const vendorFiltersStore = useVendorFiltersStore();

const { user, userDisplayName } = storeToRefs(authStore);
const { partnership, partnerships, switching } = storeToRefs(partnershipStore);
const { switchPartnership } = partnershipStore;

const TOOLS_ID = "tools";

// Admins pass through the admin bypass of hasPermissions.
const toolsVisible = computed<boolean>(() => hasPermissions([Permissions.TOOLS_ACCESS]));

const isToolsRoute = computed<boolean>(() => route.meta.workspace === "tools");

// Partnerships and Tools are one switch: exactly one entry is active.
const activeId = computed<string>(() => isToolsRoute.value ? TOOLS_ID : partnership.value);

const sections = computed<WorkspaceContextSection[]>(() => {
  const result: WorkspaceContextSection[] = [];

  // A one-partnership admin still gets the section, otherwise Tools would be a dead end.
  if (partnerships.value.length >= 2 || toolsVisible.value) {
    result.push({
      id: "partnership",
      heading: "Switch partnership",
      caption: "Partnership",
      variant: "switch",
      primary: true,
      items: partnerships.value.map(p => ({ id: p, label: PARTNERSHIP_LABELS[p] })),
    });
  }

  result.push({
    id: TOOLS_ID,
    heading: "Switch workspace",
    caption: "Workspace",
    variant: "switch",
    permission: Permissions.TOOLS_ACCESS,
    items: [{ id: TOOLS_ID, label: "Tools" }],
  });

  return result;
});

const canAccess = (permission: string): boolean => hasPermissions([permission]);

const contextUser = computed<WorkspaceContextUser | null>(() =>
  user.value
    ? {
      name: user.value.name,
      email: user.value.email,
      role: user.value.role,
      avatar: user.value.avatar,
      displayName: userDisplayName.value,
    }
    : null,
);

const isPartnership = (id: string): id is Partnership =>
  partnerships.value.some(p => p === id);

// Partnerships are separate analysis domains — start the new one with a clean
// filter slate. Reset BEFORE the switch: shared filters (dates/channels/
// granularity) live in the global store, so the new environment's requests
// must not read the previous one's values after partnership flips. Awaiting
// the URL reset also lands it before the redirect, so router.beforeEach
// does not re-inject the persisted filter params.
const applyPartnership = async (target: Partnership): Promise<boolean> => {
  vendorFiltersStore.resetSelection();
  await filtersStore.resetAllFilters();

  return switchPartnership(target);
};

const onSwitch = async (id: string) => {
  if (!isPartnership(id)) return;

  const target = id;
  const switched = await applyPartnership(target);
  if (!switched) return;

  // The switch happens without navigation, so authGuard never re-runs — repeat
  // its checks here: the page must belong to the new partnership AND the user
  // must hold its effective permissions (vendorPermissions in Vendor Central).
  // Otherwise land on the first accessible route of the new partnership.
  const meta = route.meta as RouteMeta;
  const canStay = routeMatchesPartnership(meta.partnerships, target)
    && hasPermissions(getEffectivePermissions(meta, target));

  if (!canStay) {
    router.replace(findFirstAccessibleRoute(router.getRoutes()) ?? { path: "/403" });
  }
};

// Tools is valid in every partnership, so onSwitch's canStay would keep the user
// on it — leaving needs an explicit redirect.
const leaveTools = async (id: string) => {
  if (!isPartnership(id)) return;

  // The stored partnership is the one before Tools: no switch request for it.
  if (id !== partnership.value && !(await applyPartnership(id))) return;

  router.replace(findFirstAccessibleRoute(router.getRoutes()) ?? { path: "/403" });
};

const onSelect = (sectionId: string, itemId: string) => {
  if (sectionId === TOOLS_ID) {
    // By path, not by route name: insights features do not import the Tools workspace.
    router.push({ path: "/tools" });
    return;
  }

  if (isToolsRoute.value) {
    void leaveTools(itemId);
    return;
  }

  void onSwitch(itemId);
};

const goToPasswordChange = () => {
  router.push({ name: RouteNames.PASSWORD_CHANGE });
};
</script>

<template>
  <VWorkspaceContext
    :active-id
    :can-access
    :collapsed
    :placement
    :sections
    :switching
    :user="contextUser"
    caption="Partnership"
    @logout="authStore.logout()"
    @profile="goToPasswordChange"
    @select="onSelect"
  />
</template>
